import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useCallback, useEffect, useRef, useState } from 'react'

import { REFRESH_REPORT } from '../labels'
import { api, ApiError } from '../lib/api'
import { shortDate } from '../lib/format'
import type { RefreshReport } from '../lib/types'
import { FONT_MONO, INK_FAINT, INK_SOFT, LINE, RISK_COLOR, serifNumber } from '../theme'

interface Props {
  cycleId: string
  period: string
}

/** Cuántas alertas se listan aquí; el resto está en el reporte. */
const SHOWN_ALERTS = 5

/** `uploaded_at` es un timestamp completo; los formateadores esperan YYYY-MM-DD. */
function dayOf(iso: string | null): string | null {
  return iso ? iso.slice(0, 10) : null
}

function hours(seconds: number | null | undefined): string {
  if (seconds == null) return '—'
  const h = seconds / 3600
  return h >= 1 ? `${h.toFixed(1)} h` : `${Math.round(seconds / 60)} min`
}

/**
 * El reporte de refresh (BL-050) de esta corrida.
 *
 * El reporte se genera en la máquina que corre el refresh, así que llega aquí
 * subido a mano: el archivo trae su propio resumen incrustado y el backend lo
 * lee de ahí. Lo que se muestra es lo que pide atención —cuántas alertas, las
 * primeras, y en qué se fue el tiempo—; la tabla por tabla está en el reporte.
 */
export function RefreshReportCard({ cycleId, period }: Props) {
  const [reports, setReports] = useState<RefreshReport[] | null>(null)
  const [busy, setBusy] = useState<'upload' | 'open' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    try {
      setReports((await api.refreshReports(cycleId, period)).reports)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    }
  }, [cycleId, period])

  useEffect(() => {
    void load()
  }, [load])

  async function upload(file: File) {
    setBusy('upload')
    setError(null)
    try {
      await api.uploadRefreshReport(cycleId, period, file)
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusy(null)
      if (input.current) input.current.value = ''
    }
  }

  async function open(reportId: string) {
    // La pestaña se abre ANTES de la descarga: abrirla después de un await la
    // convierte en un popup que el navegador bloquea.
    const tab = window.open('', '_blank')
    setBusy('open')
    setError(null)
    try {
      const blob = await api.refreshReportBlob(cycleId, period, reportId)
      const url = URL.createObjectURL(blob)
      if (tab) tab.location.href = url
      else window.location.href = url
    } catch (e) {
      tab?.close()
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  const latest = reports?.[0]
  const h = latest?.headline

  return (
    <Box sx={{ mt: 3.5, pt: 3, borderTop: `1px solid ${LINE}` }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h2">{REFRESH_REPORT.title}</Typography>
        <Stack direction="row" spacing={1}>
          {latest && (
            <Button
              size="small"
              variant="contained"
              startIcon={<OpenInNewIcon />}
              disabled={busy !== null}
              onClick={() => void open(latest.report_id)}
            >
              {busy === 'open' ? REFRESH_REPORT.opening : REFRESH_REPORT.open}
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={busy !== null}
            onClick={() => input.current?.click()}
          >
            {busy === 'upload' ? REFRESH_REPORT.uploading : REFRESH_REPORT.upload}
          </Button>
          <input
            ref={input}
            type="file"
            accept=".html,text/html"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void upload(file)
            }}
          />
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {reports !== null && !latest && (
        <Box>
          <Typography sx={{ color: INK_SOFT }}>{REFRESH_REPORT.none}</Typography>
          <Typography sx={{ color: INK_FAINT, fontSize: '0.8125rem', mt: 0.5 }}>
            {REFRESH_REPORT.howTo}
          </Typography>
        </Box>
      )}

      {latest && h && (
        <Box>
          <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT }}>
            {h.label} · {shortDate(dayOf(latest.uploaded_at))} · {REFRESH_REPORT.uploadedBy}{' '}
            {latest.uploaded_by ?? '—'} · git {h.git_commit}
          </Typography>

          <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ mt: 2, gap: 4 }}>
            <ReportMetric
              label={REFRESH_REPORT.alerts}
              hint={REFRESH_REPORT.alertsHint}
              value={String(h.n_alerts)}
              color={h.n_alerts > 0 ? RISK_COLOR.at_risk : undefined}
            />
            <ReportMetric
              label={REFRESH_REPORT.standing}
              hint={REFRESH_REPORT.standingHint}
              value={String(h.n_standing)}
            />
            <ReportMetric label={REFRESH_REPORT.tables} value={String(h.n_tables)} />
            <ReportMetric
              label={REFRESH_REPORT.runtime}
              hint={REFRESH_REPORT.runtimeHint}
              value={hours(h.runtime?.clean_seconds)}
            />
          </Stack>

          {h.n_alerts === 0 ? (
            <Alert severity="success" sx={{ mt: 2.5 }}>
              {REFRESH_REPORT.noAlerts}
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mt: 2.5 }}>
              <Stack spacing={0.5}>
                {h.alerts.slice(0, SHOWN_ALERTS).map((a, i) => (
                  <Typography key={i} sx={{ fontSize: '0.8125rem' }}>
                    <b>
                      {a.market} · {a.table}
                    </b>{' '}
                    — {a.message}
                  </Typography>
                ))}
                {h.n_alerts > SHOWN_ALERTS && (
                  <Typography sx={{ fontSize: '0.8125rem', color: INK_SOFT }}>
                    +{h.n_alerts - SHOWN_ALERTS} {REFRESH_REPORT.moreAlerts}
                  </Typography>
                )}
              </Stack>
            </Alert>
          )}

          {h.runtime && h.runtime.top_stages.length > 0 && (
            <Box sx={{ mt: 2.5 }}>
              <Typography sx={{ fontSize: '0.75rem', color: INK_FAINT, mb: 1 }}>
                {REFRESH_REPORT.topStages}
              </Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                {h.runtime.top_stages.map((s) => (
                  <Chip
                    key={`${s.driver}-${s.stage}`}
                    size="small"
                    variant="outlined"
                    label={`${s.driver} · ${s.stage} · ${hours(s.clean_seconds)}${
                      s.share != null ? ` (${Math.round(s.share * 100)}%)` : ''
                    }`}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {h.versions.length > 0 && (
            <Box sx={{ mt: 2.5 }}>
              <Typography sx={{ fontSize: '0.75rem', color: INK_FAINT, mb: 1 }}>
                {REFRESH_REPORT.versions}
              </Typography>
              <Stack spacing={0.25}>
                {h.versions.map((v) => (
                  <Typography
                    key={`${v.market}-${v.layer}`}
                    sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_SOFT }}
                  >
                    {v.market} · {v.layer}: {v.baseline ?? '—'} → {v.version}
                    {v.gate ? ` · ${REFRESH_REPORT.gate} ${v.gate}` : ''}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {reports && reports.length > 1 && (
            <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ mt: 2.5, gap: 1 }} alignItems="center">
              <Typography sx={{ fontSize: '0.75rem', color: INK_FAINT }}>
                {REFRESH_REPORT.previous}:
              </Typography>
              {reports.slice(1).map((r) => (
                <Chip
                  key={r.report_id}
                  size="small"
                  label={`${r.label ?? r.report_id} · ${shortDate(dayOf(r.uploaded_at))}`}
                  onClick={() => void open(r.report_id)}
                />
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  )
}

function ReportMetric({
  label,
  value,
  hint,
  color,
}: {
  label: string
  value: string
  hint?: string
  color?: string
}) {
  return (
    <Box>
      <Typography sx={{ ...serifNumber, fontSize: '1.75rem', lineHeight: 1.1, color }}>{value}</Typography>
      <Typography sx={{ fontSize: '0.75rem', color: INK_SOFT }}>{label}</Typography>
      {hint && <Typography sx={{ fontSize: '0.6875rem', color: INK_FAINT }}>{hint}</Typography>}
    </Box>
  )
}
